import Events from '@isyscore/classes/events';
import { arrayEach } from '@isyscore/utils/array';
import { isArray, isString, isRegExp } from '@isyscore/utils/type';
import { urlDelParams } from '@isyscore/utils/url';

const SIGNATURE = 'ISC_OS';

const framed = window.top !== window;
const send = (channel, payload = {}) => {
    const message = {
        SIGNATURE,
        channel,
        payload
    };
    window.top.postMessage(JSON.stringify(message), '*');
};
const injectRouter = (router) => {
    if (!router || !router.beforeEach) {
        throw new Error('请将 vueRouter 实例传入到');
    }
    if (window.top !== window.parent || window.parent === window.self) {
        return;
    }
    router.beforeEach((to, from, next) => {
        send('navigator:url', { base: router.options.base, url: to.fullPath, type: router.mode });
        next();
    });
};
const collectUserVisitAction = (router) => {
    if (!router || !router.beforeEach) {
        throw new Error('请将 vueRouter 实例传入到');
    }
    if (window.top !== window.parent || window.parent === window.self) {
        return;
    }
    router.beforeEach((to, from, next) => {
        const pageCode = to.meta && to.meta.pageCode;
        if (!pageCode)
            return next();
        window.__ISYSCORE_SDK__.record({
            type: 'visit',
            appCode: getAppCode(router.options.base),
            pageCode: to.meta.pageCode,
            actionCode: 'visit',
            times: 1
        });
        return next();
    });
    function getAppCode(routerBase) {
        if (!routerBase)
            return '';
        const reg1 = /^\/.+\/(.+)\/$/;
        const reg2 = /^\/([^/]+)\/$/;
        if (reg1.test(routerBase))
            return reg1.exec(routerBase)[1];
        if (reg2.test(routerBase))
            return reg2.exec(routerBase)[1];
        return '';
    }
};
const userLogin = () => {
    throw new Error('请使用 ssoAuth 来实现');
};
const userLogout = () => send('user:logout');
const topBarShow = () => send('topBar:show');
const topBarHide = () => send('topBar:hide');
const navigatorHome = () => send('navigator:home');
const navigatorLoginPage = () => send('navigator:loginPage');
const navigatorRefresh = () => send('navigator:refresh');
const navigatorCommonSettingDialog = () => send('navigator:commonSettingDialog');
const navigatorApp = (appCode, to = '/') => send('navigator:app', { appCode, to });
const appClose = () => send('app:close');
const licenseInvalid = () => send('license:invalid');
const reportUserAction = (userAction) => {
    window.__ISYSCORE_SDK__.record(userAction);
};

const OS_APP = 'os';
const THIRD_APP = 'app';
const getNamespace = () => {
    const pathnames = location.pathname.split(/\//).filter((s) => !!s);
    const [firstName, secondName] = pathnames;
    if (firstName === OS_APP || firstName === THIRD_APP)
        return secondName;
    return firstName || `<${Date.now()}>`;
};
const createStorage = (storage, type) => {
    const namespaced = type === 'desk' ? 'ROOT:desk' : 'APP:' + getNamespace();
    const genKey = (key) => [SIGNATURE, namespaced, key].join(':');
    const setItem = (key, val) => storage.setItem(genKey(key), val);
    const getItem = (key) => storage.getItem(genKey(key));
    const removeItem = (key) => storage.removeItem(genKey(key));
    const clear = () => storage.clear();
    return { setItem, getItem, removeItem, clear };
};

const local = createStorage(localStorage, 'app');
const session = createStorage(sessionStorage, 'app');

var app = /*#__PURE__*/Object.freeze({
    __proto__: null,
    framed: framed,
    send: send,
    userLogin: userLogin,
    userLogout: userLogout,
    topBarShow: topBarShow,
    topBarHide: topBarHide,
    navigatorHome: navigatorHome,
    navigatorLoginPage: navigatorLoginPage,
    navigatorRefresh: navigatorRefresh,
    navigatorApp: navigatorApp,
    navigatorCommonSettingDialog: navigatorCommonSettingDialog,
    appClose: appClose,
    licenseInvalid: licenseInvalid,
    injectRouter: injectRouter,
    collectUserVisitAction: collectUserVisitAction,
    reportUserAction: reportUserAction,
    localStorage: local,
    sessionStorage: session
});

const event = new Events();
window.addEventListener('message', (ev) => {
    const { data } = ev;
    let message;
    try {
        message = JSON.parse(data);
        const { SIGNATURE: s, channel, payload } = message;
        // 非签名事件
        if (s !== SIGNATURE)
            return;
        event.emit(channel, payload, ev);
    }
    catch (err) {
        // ignore
    }
});
const receive = (channel, listener) => {
    event.on(channel, listener);
};

const local$1 = createStorage(localStorage, 'desk');
const session$1 = createStorage(sessionStorage, 'desk');

var desk = /*#__PURE__*/Object.freeze({
    __proto__: null,
    receive: receive,
    localStorage: local$1,
    sessionStorage: session$1
});

// 检查 configs
const testConfigs = (configs) => {
    const defaults = {
        ssoApi: '/api/permission/auth/status',
        ssoUrl: '/sso/',
        ssoTokenStorage: localStorage,
        ssoTokenKey: 'ISC_SSO_TOKEN',
        matcher: location.pathname,
        excludes: []
    };
    const finalConfigs = Object.assign(defaults, configs);
    const msgFn = (name) => `'@isyscore/messenger:ssoAuth 需要传入 ${name}'`;
    if (!finalConfigs.axios) {
        const message = msgFn('axios 实例');
        alert(message);
        throw new Error(message);
    }
    if (!finalConfigs.store) {
        const message = msgFn('store 实例');
        alert(message);
        throw new Error(message);
    }
    return finalConfigs;
};
// 接口请求
const service = async (configs, token) => {
    const res = await configs.axios.get(configs.ssoApi, { headers: { token } });
    return res.data || res;
};
// 跳转到 sso，获取登录凭证
const location2sso = (configs) => {
    const reference = urlDelParams(location.href, ['return']);
    const reten = encodeURIComponent(reference);
    location.replace(`${configs.ssoUrl}?return=${reten}`);
};
const resgisteStore = (configs, state) => {
    const namespace = 'sso';
    configs.store.registerModule(namespace, {
        namespaced: true,
        state() {
            return state;
        }
    });
};
/**
 * 是否必须验证
 * @param {string} matcher
 * @param {MatchUnits} excludes
 * @returns {boolean}
 */
function shouldAuth(matcher, excludes) {
    if (!isArray(excludes))
        excludes = [excludes];
    const matchUnit = (unit) => {
        if (isString(unit)) {
            return matcher === unit;
        }
        else if (isRegExp(unit)) {
            return unit.test(matcher);
        }
        else {
            return unit(matcher);
        }
    };
    const matchUnits = (units, exclude) => {
        let matched = false;
        arrayEach(units, (unit) => {
            // 匹配
            if (matchUnit(unit)) {
                matched = true;
                return false;
            }
        });
        return matched;
    };
    // 如果有被排除，则不必须
    if (matchUnits(excludes))
        return false;
    return true;
}
async function ssoAuth(configs) {
    // 0、检查 configs
    const finalConfigs = testConfigs(configs);
    const { matcher, ssoTokenStorage, ssoTokenKey } = finalConfigs;
    let { excludes } = finalConfigs;
    if (!isArray(excludes))
        excludes = [excludes];
    // 0.1 -> 免验证流程
    if (!shouldAuth(matcher, excludes))
        return;
    // 0.2 -> 进入验证流程
    // 1.1、从 url 上获取 token
    const sp = new URLSearchParams(location.search);
    let token = sp.get('token');
    // 1.2、从本地存储获取
    if (!token) {
        token = ssoTokenStorage.getItem(ssoTokenKey);
    }
    // 2、没有凭证跳转登录
    if (!token) {
        location2sso(finalConfigs);
        throw new Error('没有登录凭证');
    }
    // 3、有 token，清除 token/return
    history.replaceState(null, '', urlDelParams(location.href, ['token', 'return']));
    // 4、获取认证状态数据
    let ssoAuthState;
    try {
        ssoAuthState = await service(finalConfigs, token);
    }
    catch (err) {
        location2sso(finalConfigs);
        throw err;
    }
    // 5、注册 store
    resgisteStore(finalConfigs, ssoAuthState);
    // 6、 返回认证状态数据
    return ssoAuthState;
}

export { app, desk, ssoAuth };
